import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Gift, DollarSign, Clock, Heart, Loader2, StickyNote } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useMyThreads } from '@/hooks/useConnectApi';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';


const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface WorkingDay {
  day: string;
  enabled: boolean;
  start: string;
  end: string;
}

const defaultWorkingHours: WorkingDay[] = DAYS_OF_WEEK.map((day, i) => ({
  day,
  enabled: false,
  start: '08:00',
  end: '18:00',
}));

/**
 * The authorization role this offer will confer, chosen BY THE SHOP.
 *
 * This is the shop's decision, not a reflection of what the recipient says
 * about themselves. `accept-connect-offer` reads `connect_offers.offered_role`
 * and nothing else when it writes `memberships.role`; the database refuses to
 * let the recipient change this field at all (trigger
 * `connect_offers_shop_authored_role_guard`).
 *
 * Deliberately narrower than the full `app_role` enum: OWNER and ORG_ADMIN are
 * ownership and tenancy administration rather than employment, and
 * REGION_MANAGER / DISTRICT_MANAGER reach across shops that one shop does not
 * hold. A CHECK constraint enforces the same narrowing in the database.
 */
const OFFERABLE_ROLES: { value: string; label: string; hint: string }[] = [
  { value: 'TECHNICIAN',     label: 'Technician',     hint: 'Works tickets in a bay' },
  // WS-ELIG B-E7 (CORE-R:82 — the shop controls its offers): detail work. Not
  // an app_role; stored as STAFF + offered_work_roles {DETAIL_SPECIALIST}.
  { value: 'DETAIL_SPECIALIST', label: 'Detail Specialist', hint: 'Details vehicles (detail work only)' },
  { value: 'SERVICE_WRITER', label: 'Service Writer', hint: 'Advises customers and writes tickets' },
  { value: 'MANAGER',        label: 'Manager',        hint: 'Runs the shop — full operational authority' },
  { value: 'DISPATCHER',     label: 'Dispatcher',     hint: 'Assigns and sequences work' },
  { value: 'PARTS',          label: 'Parts',          hint: 'Manages parts ordering and receiving' },
  { value: 'AUDITOR',        label: 'Auditor',        hint: 'Read-only review access' },
];

/**
 * WS-ELIG B-E7: the offer columns for a role choice. A work offer with no
 * app_role (Detail Specialist) confers the LEAST authority (STAFF) and states
 * the work in `offered_work_roles`; every other choice is unchanged.
 */
export function offerRoleColumns(choice: string): { offered_role: string; offered_work_roles: string[] | null } {
  if (choice === 'DETAIL_SPECIALIST') return { offered_role: 'STAFF', offered_work_roles: ['DETAIL_SPECIALIST'] };
  return { offered_role: choice, offered_work_roles: null };
}

/**
 * WS-GAPS2 B-E4 (CORE-W:45): only an offer of roster work (Technician / Detail Specialist) can take over a
 * pre-seeded roster entry — the same rule the database guard (connect_offers_roster_entry_guard) enforces.
 */
export function offerAdoptsRosterEntry(choice: string): boolean {
  return choice === 'TECHNICIAN' || choice === 'DETAIL_SPECIALIST';
}

/** The offer column for the "already on your roster?" choice ('none' / '' = a new person). */
export function offerRosterEntryColumn(choice: string, entry: string): { technician_id?: string } {
  return offerAdoptsRosterEntry(choice) && entry && entry !== 'none' ? { technician_id: entry } : {};
}

/** Resolve user display names for thread participants */
function useThreadUserNames(userIds: string[]) {
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (userIds.length === 0) return;
    const unique = [...new Set(userIds)];
    supabase
      .from('profiles')
      .select('id, display_name, email')
      .in('id', unique)
      .then(({ data }) => {
        if (data) {
          const map: Record<string, string> = {};
          data.forEach((p: { id: string; display_name: string | null; email: string | null }) => {
            map[p.id] = p.display_name || p.email || 'User';
          });
          setNames(map);
        }
      });
  }, [userIds.join(',')]);
  return names;
}

export interface OfferPrefill {
  user_id: string;
  pay_type: string | null;
  pay_rate: number | null;
  bonus_criteria: string | null;
  working_hours: any;
  health_insurance: boolean;
  dental_insurance: boolean;
  vision_insurance: boolean;
  life_insurance: boolean;
  retirement_401k: boolean;
  retirement_match: string | null;
  pto_days: number | null;
  uniforms_provided: boolean;
  tools_provided: boolean;
  additional_benefits: string | null;
  notes: string | null;
  lunch_break_start: string | null;
  lunch_break_end: string | null;
}

export function OfferBuilder({ prefill, onPrefillConsumed }: { prefill?: OfferPrefill | null; onPrefillConsumed?: () => void }) {
  const { activeShop } = useAuth();
  const qc = useQueryClient();
  const { data: threads } = useMyThreads();

  // Only show threads belonging to this shop
  const shopThreads = threads?.filter(t => t.shop_id === activeShop?.id) || [];
  const connectedUserIds = shopThreads.map(t => t.user_id);
  const userNames = useThreadUserNames(connectedUserIds);

  const [selectedUserId, setSelectedUserId] = useState('');
  // No default. An offer's authority is a decision somebody has to make, and a
  // pre-selected value is a decision nobody made.
  const [offeredRole, setOfferedRole] = useState<string>('');
  // WS-GAPS2 B-E4: the shop's statement that this person is already on its roster (an unjoined entry).
  const [rosterEntry, setRosterEntry] = useState<string>('none');
  const [unclaimedRoster, setUnclaimedRoster] = useState<{ id: string; name: string }[]>([]);
  const [payType, setPayType] = useState<string>('');
  const [payRate, setPayRate] = useState('');
  const [bonusCriteria, setBonusCriteria] = useState('');
  const [workingHours, setWorkingHours] = useState<WorkingDay[]>(defaultWorkingHours);

  // Benefits
  const [healthIns, setHealthIns] = useState(false);
  const [dentalIns, setDentalIns] = useState(false);
  const [visionIns, setVisionIns] = useState(false);
  const [lifeIns, setLifeIns] = useState(false);
  const [retirement, setRetirement] = useState(false);
  const [retirementMatch, setRetirementMatch] = useState('');
  const [ptoDays, setPtoDays] = useState('');
  const [uniforms, setUniforms] = useState(false);
  const [tools, setTools] = useState(false);
  const [additionalBenefits, setAdditionalBenefits] = useState('');
  const [offerNotes, setOfferNotes] = useState('');
  const [lunchStart, setLunchStart] = useState('');
  const [lunchEnd, setLunchEnd] = useState('');

  const [saving, setSaving] = useState(false);

  // Populate from prefill (counter offer)
  useEffect(() => {
    if (!prefill) return;
    setSelectedUserId(prefill.user_id);
    setPayType(prefill.pay_type || '');
    setPayRate(prefill.pay_rate != null ? prefill.pay_rate.toLocaleString() : '');
    setBonusCriteria(prefill.bonus_criteria || '');
    if (Array.isArray(prefill.working_hours)) setWorkingHours(prefill.working_hours);
    setHealthIns(prefill.health_insurance);
    setDentalIns(prefill.dental_insurance);
    setVisionIns(prefill.vision_insurance);
    setLifeIns(prefill.life_insurance);
    setRetirement(prefill.retirement_401k);
    setRetirementMatch(prefill.retirement_match || '');
    setPtoDays(prefill.pto_days != null ? String(prefill.pto_days) : '');
    setUniforms(prefill.uniforms_provided);
    setTools(prefill.tools_provided);
    setAdditionalBenefits(prefill.additional_benefits || '');
    setOfferNotes(prefill.notes || '');
    setLunchStart(prefill.lunch_break_start || '');
    setLunchEnd(prefill.lunch_break_end || '');
    onPrefillConsumed?.();
  }, [prefill]);

  // WS-GAPS2 B-E4: roster entries nobody has joined as yet (the same list Team Management offers an invitation).
  // The database refuses anything else (another shop's row, a joined row, one bound to an account).
  const adoptsRoster = offerAdoptsRosterEntry(offeredRole);
  useEffect(() => {
    if (!adoptsRoster) { setRosterEntry('none'); return; }
    if (!activeShop?.id) return;
    let cancelled = false;
    (async () => {
      const [{ data: techs }, { data: bound }] = await Promise.all([
        supabase.from('technicians').select('id, name').eq('shop_id', activeShop.id).eq('employment_status', 'Active').order('name'),
        supabase.from('technician_users').select('technician_id').eq('shop_id', activeShop.id),
      ]);
      if (cancelled) return;
      const boundIds = new Set(((bound ?? []) as { technician_id: string }[]).map((b) => b.technician_id));
      setUnclaimedRoster(((techs ?? []) as { id: string; name: string }[]).filter((t) => !boundIds.has(t.id)));
    })();
    return () => { cancelled = true; };
  }, [adoptsRoster, activeShop?.id]);

  const toggleDay = (index: number) => {
    setWorkingHours(prev => prev.map((d, i) =>
      i === index ? { ...d, enabled: !d.enabled } : d
    ));
  };

  const updateHour = (index: number, field: 'start' | 'end', value: string) => {
    setWorkingHours(prev => prev.map((d, i) =>
      i === index ? { ...d, [field]: value } : d
    ));
  };

  const handleSave = async () => {
    if (!selectedUserId || !activeShop?.id) {
      toast.error('Please select a connected user');
      return;
    }
    if (!offeredRole) {
      toast.error('Choose the role this offer is for');
      return;
    }
    setSaving(true);
    try {
      const matchingThread = shopThreads.find(t => t.user_id === selectedUserId);
      const { error } = await supabase.from('connect_offers').insert({
        shop_id: activeShop.id,
        user_id: selectedUserId,
        thread_id: matchingThread?.id || null,
        ...offerRoleColumns(offeredRole),
        ...offerRosterEntryColumn(offeredRole, rosterEntry),
        pay_type: payType || null,
        pay_rate: payRate ? parseFloat(payRate.replace(/,/g, '')) : null,
        bonus_criteria: bonusCriteria || null,
        working_hours: workingHours,
        lunch_break_start: lunchStart || null,
        lunch_break_end: lunchEnd || null,
        health_insurance: healthIns,
        dental_insurance: dentalIns,
        vision_insurance: visionIns,
        life_insurance: lifeIns,
        retirement_401k: retirement,
        retirement_match: retirementMatch || null,
        pto_days: ptoDays ? parseInt(ptoDays) : null,
        uniforms_provided: uniforms,
        tools_provided: tools,
        additional_benefits: additionalBenefits || null,
        notes: offerNotes || null,
        status: 'sent',
      } as any);
      if (error) throw error;
      toast.success('🎁 Offer Sent!', { description: `Your offer has been sent to ${userNames[selectedUserId] || 'the user'}.` });
      qc.invalidateQueries({ queryKey: ['connect-offers-sent'] });
      qc.invalidateQueries({ queryKey: ['connect-offers-received'] });
      // Reset form
      setSelectedUserId('');
      setOfferedRole('');
      setRosterEntry('none');
      setPayType('');
      setPayRate('');
      setBonusCriteria('');
      setWorkingHours(defaultWorkingHours);
      setHealthIns(false);
      setDentalIns(false);
      setVisionIns(false);
      setLifeIns(false);
      setRetirement(false);
      setRetirementMatch('');
      setPtoDays('');
      setUniforms(false);
      setTools(false);
      setAdditionalBenefits('');
      setOfferNotes('');
      setLunchStart('');
      setLunchEnd('');
    } catch (err: any) {
      toast.error('Failed to send offer', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Deduplicate user IDs (a user could have multiple threads theoretically)
  const uniqueUsers = [...new Map(shopThreads.map(t => [t.user_id, t])).values()];

  return (
    <Card id="offer-builder">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Make an Offer</CardTitle>
        </div>
        <CardDescription>Send an offer to a connected user with pay, hours, and benefits details.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* User selector */}
        <div className="space-y-2">
          <Label>Select Connected User</Label>
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a connected user…" />
            </SelectTrigger>
            <SelectContent>
              {uniqueUsers.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">No connected users yet</div>
              ) : (
                uniqueUsers.map(t => (
                  <SelectItem key={t.user_id} value={t.user_id}>
                    {userNames[t.user_id] || 'User'}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Role — the shop's authorization decision */}
        <div className="space-y-2">
          <Label htmlFor="offered-role">Role at this shop</Label>
          <Select value={offeredRole} onValueChange={setOfferedRole}>
            <SelectTrigger id="offered-role">
              <SelectValue placeholder="Choose the role you are offering…" />
            </SelectTrigger>
            <SelectContent>
              {OFFERABLE_ROLES.map(r => (
                <SelectItem key={r.value} value={r.value}>
                  <span className="font-medium">{r.label}</span>
                  <span className="text-muted-foreground"> — {r.hint}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            This is what the person will be able to do in your shop. It is set by you
            and cannot be changed by them.
          </p>
        </div>

        {/* WS-GAPS2 B-E4 (CORE-W:45): name the existing roster entry so acceptance takes it over (and the work
            already recorded under it) instead of creating a second one. They see this before accepting. */}
        {adoptsRoster && unclaimedRoster.length > 0 && (
          <div className="space-y-2">
            <Label htmlFor="offer-roster-entry">Already on your roster?</Label>
            <Select value={rosterEntry} onValueChange={setRosterEntry}>
              <SelectTrigger id="offer-roster-entry" data-testid="offer-roster-entry-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No — this is a new person</SelectItem>
                {unclaimedRoster.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Choose their roster entry and, when they accept, it becomes theirs with the work already recorded
              under it. They are shown this before they accept.
            </p>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="pay" className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="pay" className="flex-1 gap-1.5">
              <DollarSign className="h-3.5 w-3.5" /> Pay Structure
            </TabsTrigger>
            <TabsTrigger value="hours" className="flex-1 gap-1.5">
              <Clock className="h-3.5 w-3.5" /> Hours
            </TabsTrigger>
            <TabsTrigger value="benefits" className="flex-1 gap-1.5">
              <Heart className="h-3.5 w-3.5" /> Benefits
            </TabsTrigger>
            <TabsTrigger value="notes" className="flex-1 gap-1.5">
              <StickyNote className="h-3.5 w-3.5" /> Notes
            </TabsTrigger>
          </TabsList>

          {/* PAY STRUCTURE */}
          <TabsContent value="pay" className="mt-4 space-y-4">
            <div className="space-y-3">
              <Label className="text-sm font-medium">Pay Type</Label>
              <div className="flex flex-col gap-2">
                {[
                  { value: 'hourly', label: 'Hourly' },
                  { value: 'salary', label: 'Salary' },
                  { value: 'flat_rate', label: 'Flat Rate' },
                ].map(opt => (
                  <div key={opt.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`pay-${opt.value}`}
                      checked={payType === opt.value}
                      onCheckedChange={(checked) => setPayType(checked ? opt.value : '')}
                    />
                    <label htmlFor={`pay-${opt.value}`} className="text-sm">{opt.label}</label>
                  </div>
                ))}
              </div>
            </div>

            {payType && (
              <div className="space-y-2">
                <Label>
                  {payType === 'hourly' ? 'Hourly Rate ($)' : payType === 'salary' ? 'Annual Salary ($)' : 'Flat Rate ($)'}
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 65,000.00"
                  value={payRate}
                  onChange={e => {
                    const v = e.target.value.replace(/[^0-9.,]/g, '');
                    setPayRate(v);
                  }}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Bonus Criteria</Label>
              <Textarea
                placeholder="Describe bonus structure, performance criteria, etc."
                value={bonusCriteria}
                onChange={e => setBonusCriteria(e.target.value)}
                rows={3}
              />
            </div>
          </TabsContent>

          {/* HOURS */}
          <TabsContent value="hours" className="mt-4 space-y-3">
            {workingHours.map((day, index) => (
              <div key={day.day} className="space-y-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`offer-day-${index}`}
                    checked={day.enabled}
                    onCheckedChange={() => toggleDay(index)}
                  />
                  <label htmlFor={`offer-day-${index}`} className="font-medium w-28">
                    {day.day}
                  </label>
                  {day.enabled && (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        type="time"
                        value={day.start}
                        onChange={e => updateHour(index, 'start', e.target.value)}
                        className="w-32"
                      />
                      <span className="text-muted-foreground text-sm">to</span>
                      <Input
                        type="time"
                        value={day.end}
                        onChange={e => updateHour(index, 'end', e.target.value)}
                        className="w-32"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Lunch Break */}
            <div className="pt-4 border-t space-y-2">
              <Label className="text-sm font-medium">Lunch Break</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={lunchStart}
                  onChange={e => setLunchStart(e.target.value)}
                  className="w-32"
                  placeholder="Start"
                />
                <span className="text-muted-foreground text-sm">to</span>
                <Input
                  type="time"
                  value={lunchEnd}
                  onChange={e => setLunchEnd(e.target.value)}
                  className="w-32"
                  placeholder="End"
                />
              </div>
              <p className="text-xs text-muted-foreground">Optional. Sets a daily recurring lunch break on the technician's schedule.</p>
            </div>
          </TabsContent>

          {/* BENEFITS */}
          <TabsContent value="benefits" className="mt-4 space-y-5">
            {/* Insurance */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Insurance</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { state: healthIns, setter: setHealthIns, label: 'Health Insurance' },
                  { state: dentalIns, setter: setDentalIns, label: 'Dental Insurance' },
                  { state: visionIns, setter: setVisionIns, label: 'Vision Insurance' },
                  { state: lifeIns, setter: setLifeIns, label: 'Life Insurance' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-2">
                    <Checkbox
                      checked={item.state}
                      onCheckedChange={(c) => item.setter(!!c)}
                    />
                    <span className="text-sm">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Retirement */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Retirement</Label>
              <div className="flex items-center gap-2">
                <Checkbox checked={retirement} onCheckedChange={(c) => setRetirement(!!c)} />
                <span className="text-sm">401(k) / Retirement Plan</span>
              </div>
              {retirement && (
                <div className="space-y-1 pl-6">
                  <Label className="text-xs">Match Details</Label>
                  <Input
                    placeholder="e.g. 4% match"
                    value={retirementMatch}
                    onChange={e => setRetirementMatch(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* PTO */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">PTO (Paid Time Off)</Label>
              <Input
                type="number"
                placeholder="Days per year"
                value={ptoDays}
                onChange={e => setPtoDays(e.target.value)}
                className="w-40"
              />
            </div>

            {/* Uniforms & Tools */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Other Perks</Label>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox checked={uniforms} onCheckedChange={(c) => setUniforms(!!c)} />
                  <span className="text-sm">Uniforms Provided</span>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={tools} onCheckedChange={(c) => setTools(!!c)} />
                  <span className="text-sm">Tools Provided</span>
                </div>
              </div>
            </div>

            {/* Additional */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Additional Benefits</Label>
              <Textarea
                placeholder="Any other benefits or perks you'd like to include…"
                value={additionalBenefits}
                onChange={e => setAdditionalBenefits(e.target.value)}
                rows={3}
              />
            </div>
          </TabsContent>

          {/* NOTES */}
          <TabsContent value="notes" className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Additional Notes</Label>
              <Textarea
                placeholder="Any additional information, expectations, or details you'd like to share with the candidate…"
                value={offerNotes}
                onChange={e => setOfferNotes(e.target.value)}
                rows={5}
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Submit */}
        <Button onClick={handleSave} disabled={saving || !selectedUserId || !offeredRole} className="w-full">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Gift className="h-4 w-4 mr-2" />}
          Send Offer
        </Button>
      </CardContent>
    </Card>
  );
}
